type ProviderAddresses<
  K extends string,
  A extends { address: string },
> = Partial<Record<K, readonly A[]>>;

const entriesOf = <K extends string, A extends { address: string }>(
  addresses: ProviderAddresses<K, A>
) =>
  // Object.entries widens the key to string; the keys are exactly K.
  Object.entries(addresses) as [K, readonly A[]][];

const holds = (accounts: readonly { address: string }[], address?: string) =>
  accounts.some((item) => item.address === address);

/**
 * The provider entry that owns the selected address. The same address can be
 * exposed by several wallets, so prefer the one the user selected, and only
 * fall back to the first that holds the address when that one no longer does.
 */
export const findSelectedEntry = <
  K extends string,
  A extends { address: string },
>(
  addresses: ProviderAddresses<K, A>,
  selectedAddress?: string,
  selectedProvider?: K
): [K, readonly A[]] | undefined => {
  const entries = entriesOf(addresses);
  return (
    entries.find(
      ([provider, accounts]) =>
        provider === selectedProvider && holds(accounts, selectedAddress)
    ) || entries.find(([, accounts]) => holds(accounts, selectedAddress))
  );
};

/**
 * The provider that should sign for an address. For the selected address it is
 * the selected provider (if that wallet still holds it); otherwise the first
 * connected wallet exposing the address.
 */
export const pickSignerProvider = <
  K extends string,
  A extends { address: string },
>(
  addresses: ProviderAddresses<K, A>,
  address: string,
  selectedAddress?: string,
  selectedProvider?: K
): K | undefined => {
  if (
    selectedProvider &&
    address === selectedAddress &&
    holds(addresses[selectedProvider] ?? [], address)
  )
    return selectedProvider;
  return entriesOf(addresses).find(([, accounts]) =>
    holds(accounts, address)
  )?.[0];
};
